import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface AssessmentCriteria {
  category: string;
  requirements: string[];
}

interface ReadinessAssessmentProps {
  criteria: AssessmentCriteria[];
}

const ReadinessAssessment: React.FC<ReadinessAssessmentProps> = ({ criteria }) => {
  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-4 sm:mb-6">
              Prerequisites & Readiness Assessment
            </h2>
            <p className="text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-2">
              Successful program participation requires institutional readiness across strategic clarity, organizational capacity, and commitment to sustained engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {criteria.map((criterion, index) => (
              <div
                key={index}
                className="bg-background rounded-lg institutional-border p-5 sm:p-8 hover:institutional-shadow transition-base"
              >
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-institutional/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <Icon name="ClipboardDocumentCheckIcon" size={18} className="text-institutional sm:hidden" />
                    <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-institutional hidden sm:block" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-text-primary">
                    {criterion.category}
                  </h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {criterion.requirements.map((requirement, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-institutional rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                      <span className="text-xs sm:text-sm text-text-secondary leading-relaxed">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 bg-white rounded-lg institutional-border p-5 sm:p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 sm:gap-8">
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold text-institutional mb-3 sm:mb-4">
                  Begin with an Initial Conversation
                </h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">
                  The program begins with a thorough diagnostic conversation to assess institutional readiness, strategic priorities, and alignment with the engagement model. This ensures mutual commitment to the systematic approach required for successful transformation.
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  This diagnostic process establishes the foundation for a customized engagement that addresses your institution's specific challenges and opportunities within the framework of proven strategic development principles.
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-auto">
                <a
                  href="/contact"
                  className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-institutional text-white font-semibold rounded-md hover:bg-institutional/90 transition-fast institutional-shadow text-sm sm:text-base"
                >
                  Request Initial Conversation
                  <Icon name="ArrowRightIcon" size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReadinessAssessment;